import { useState } from "react";
import styles from "./Designer.module.css";

/**
 * Paso "Tiras aplicadas" — dibujo libre guiado (vuelta a la metodología anterior, 2026-09):
 *   1) "Agregar tira" -> elegir Recta o Curva.
 *   2) Se dibuja sobre la prenda (recta = punto a punto, recortada a los límites de la silueta;
 *      curva = trazo libre). Al soltar, la tira queda fija.
 *   3) Tocando la tira en la prenda se cambia su color o se elimina (popover del canvas).
 *   4) "Agregar tira" de nuevo arranca otra; "No más tiras" avanza al siguiente paso.
 * El motor de dibujo/recorte/selección vive en PreviewSection; este panel solo lo maneja.
 */
const DesignerStripesStepSection = ({
    stripeColors = [],
    activeColor = "#1d4ed8",
    onSetActiveColor,
    drawArmed = false,
    drawTool = "none",
    onArm,
    onDisarm,
    strokeCount = 0,
    onFinish,
}) => {
    const [choosing, setChoosing] = useState(false);

    const palette = (
        <div className={styles.stripBlock}>
            <span className={styles.stripBlockLabel}>Color de la tira</span>
            <div className={styles.stripColorRow}>
                {stripeColors.map((c) => {
                    const code = c.code || c.valor || "#e5e5e5";
                    const isActive = String(code).toLowerCase() === String(activeColor).toLowerCase();
                    return (
                        <button
                            key={`stripe-color-${c.id ?? code}`}
                            type="button"
                            className={`${styles.stripColorDot} ${isActive ? styles.stripColorDotActive : ""}`}
                            style={{ background: code }}
                            title={c.name || c.nname || "Color"}
                            aria-label={c.name || c.nname || "Color"}
                            onClick={() => onSetActiveColor?.(code)}
                        />
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className={styles.detailsStep}>
            <div className={styles.detailsStepHead}>
                <h4 className={styles.detailsStepTitle}>Tiras aplicadas</h4>
                <p className={styles.detailsStepSubtitle}>
                    Es opcional. Agregá tiras rectas o curvas sobre la prenda. Tocá una tira dibujada para
                    cambiarle el color o borrarla.
                </p>
            </div>

            {choosing ? (
                <div className={styles.stripBlock}>
                    <span className={styles.stripBlockLabel}>¿Recta o curva?</span>
                    <div className={styles.stripChips}>
                        <button
                            type="button"
                            className={styles.stripChip}
                            onClick={() => { onArm?.("line"); setChoosing(false); }}
                        >
                            <i className="bi bi-slash-lg" aria-hidden="true" /> Recta
                        </button>
                        <button
                            type="button"
                            className={styles.stripChip}
                            onClick={() => { onArm?.("curve"); setChoosing(false); }}
                        >
                            <i className="bi bi-bezier2" aria-hidden="true" /> Curva
                        </button>
                        <button
                            type="button"
                            className={`${styles.stripChip} ${styles.stripChipGhost}`}
                            onClick={() => setChoosing(false)}
                        >
                            Cancelar
                        </button>
                    </div>
                    {palette}
                </div>
            ) : drawArmed ? (
                <div className={styles.stripBlock}>
                    <div className={styles.stripDrawingHint}>
                        <i className="bi bi-hand-index-thumb" aria-hidden="true" />
                        <span>
                            Dibujá la <strong>{drawTool === "curve" ? "curva" : "recta"}</strong> sobre la prenda.
                            {drawTool === "line" ? " Se recorta sola a los límites de la prenda." : ""}
                        </span>
                    </div>
                    {palette}
                    <button
                        type="button"
                        className={`${styles.stripChip} ${styles.stripChipGhost}`}
                        onClick={() => onDisarm?.()}
                    >
                        Cancelar
                    </button>
                </div>
            ) : (
                <>
                    <button
                        type="button"
                        className={styles.stripAddBtn}
                        onClick={() => setChoosing(true)}
                    >
                        <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar tira
                    </button>
                    {strokeCount > 0 ? (
                        <p className={styles.stripHintMuted}>
                            {strokeCount} tira{strokeCount === 1 ? "" : "s"} en esta vista · tocá una para cambiar
                            color o borrarla.
                        </p>
                    ) : null}
                    <button
                        type="button"
                        className={styles.stripFinishBtn}
                        onClick={() => onFinish?.()}
                    >
                        <i className="bi bi-check-lg" aria-hidden="true" /> No más tiras
                    </button>
                </>
            )}
        </div>
    );
};

export default DesignerStripesStepSection;
